/**
 * Recenze na Googlu (vize F2) — sdílený slovník pro čtečku exportu Takeout (admin),
 * import a správu v API a admin UI. JEDINÉ místo konstant, rolí a tvarů, které si části
 * předávají; zod schémata požadavků jsou v `schemas/googleReviews.ts`.
 *
 * Kvalifikovaná recenze (jen ta nese jméno, text a čas a jen ta se ukládá) = zároveň
 * `FIVE`, původní text po `trim` 1..`GOOGLE_REVIEW_TEXT_MAX`, jméno autora po `trim`
 * 1..`GOOGLE_REVIEW_AUTHOR_MAX` a platný čas (`isGoogleReviewTime`). Ostatní jdou do API
 * jen jako ID, hvězdy a `hasText: false`/`true` — jméno a text neopustí prohlížeč (F2.1).
 */
import type { UserRole } from "./types";

export const GOOGLE_REVIEW_ORIGINS = ["export", "manual"] as const;
export type GoogleReviewOrigin = (typeof GOOGLE_REVIEW_ORIGINS)[number];

/** Klíč Nastavení se souhrnem posledního potvrzeného exportu (kategorie `googleReviews`). */
export const GOOGLE_REVIEWS_SUMMARY_KEY = "googleReviews.summary";

/**
 * Role, které smějí nahrát a potvrdit export (F2.1: jen admin). Jeden zdroj pro bránu
 * serveru (`requireRole(...GOOGLE_IMPORT_ROLES)` v `apps/api/src/index.ts`) i admin UI
 * (`canImportGoogleReviews`); vzor `PROMO_BAR_EDIT_ROLES`.
 */
export const GOOGLE_IMPORT_ROLES: readonly UserRole[] = ["admin"];

export function canImportGoogleReviews(role: string | null | undefined): boolean {
  return typeof role === "string" && GOOGLE_IMPORT_ROLES.includes(role as UserRole);
}

/** Nejvíc recenzí v jednom požadavku importu (i nekvalifikovaných). */
export const GOOGLE_IMPORT_MAX_REVIEWS = 20_000;
/** Nejdelší původní text recenze (znaky po `trim`). */
export const GOOGLE_REVIEW_TEXT_MAX = 10_000;
/** Nejdelší jméno autora (znaky po `trim`). */
export const GOOGLE_REVIEW_AUTHOR_MAX = 200;
/** Začátek textu v seznamu adminu (kódové body `content`). */
export const GOOGLE_REVIEW_EXCERPT_MAX = 160;
export const GOOGLE_REVIEWS_LIST_DEFAULT_LIMIT = 50;
export const GOOGLE_REVIEWS_LIST_MAX_LIMIT = 500;
/** Párování ruční s exportovanou: nejvýš tolik dní mezi daty. */
export const MANUAL_MATCH_DATE_TOLERANCE_DAYS = 62;
/** Párování ruční s exportovanou: shodný začátek normalizovaného textu (znaky). */
export const MANUAL_MATCH_TEXT_PREFIX = 40;
/** ID recenze = poslední segment jejího `name` v exportu. Jiné ID čtečka vynechá a zod odmítne. */
export const GOOGLE_REVIEW_ID_PATTERN = /^[A-Za-z0-9_-]{1,200}$/;

const GOOGLE_TIME = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.\d{1,9})?Z$/;

/**
 * Čas recenze z exportu (`createTime`/`updateTime`): ISO 8601 v UTC se `Z`, 0–9 desetinných
 * míst, a SKUTEČNÉ datum a čas — `2026-02-30`, měsíc 13 ani hodina 25 neprojdou (`Date`
 * by je tiše převalil). Týž predikát používá čtečka (K1) i zod schéma importu, aby
 * recenze, kterou čtečka propustí, nemohla shodit celý import na 400.
 */
export function isGoogleReviewTime(value: unknown): boolean {
  if (typeof value !== "string") return false;
  const m = GOOGLE_TIME.exec(value);
  if (!m) return false;
  const [y, mo, d, h, mi, s] = m.slice(1, 7).map(Number);
  const t = new Date(Date.UTC(y, mo - 1, d, h, mi, s));
  return (
    t.getUTCFullYear() === y &&
    t.getUTCMonth() === mo - 1 &&
    t.getUTCDate() === d &&
    t.getUTCHours() === h &&
    t.getUTCMinutes() === mi &&
    t.getUTCSeconds() === s
  );
}

export type StarCount = 1 | 2 | 3 | 4 | 5;

/** Recenze v požadavku importu. `author`, `text` a `createTime` právě u kvalifikované, jinak chybí. */
export interface TakeoutReviewPayload {
  id: string;
  stars: StarCount;
  hasText: boolean;
  author?: string;
  text?: string;
  createTime?: string;
}

/** Lokace exportu; `locationKey` = `<locationId>` ze složky `location-<locationId>`. */
export interface TakeoutLocationRef {
  locationKey: string;
  name: string;
  placeId: string | null;
}

export interface GoogleImportRequest {
  location: TakeoutLocationRef;
  /** `YYYY-MM-DD` */
  exportDate: string;
  exportDateSource: "filename" | "upload";
  reviews: TakeoutReviewPayload[];
}

/** Souhrn náhledu i výsledek potvrzení (M1): počty celé lokace a co import udělá. */
export interface GoogleImportPreview {
  location: TakeoutLocationRef;
  exportDate: string;
  total: number;
  score: number | null;
  counts: Record<StarCount, number>;
  added: number;
  changed: number;
  deleted: number;
  manualPaired: number;
  manualAmbiguous: number;
  skippedRemoved: number;
}

/** Hodnota klíče `googleReviews.summary` (JSON). */
export interface GoogleExportSummary {
  locationKey: string;
  locationName: string;
  placeId: string | null;
  exportDate: string;
  exportDateSource: "filename" | "upload";
  importedAt: string;
  importedBy: string | null;
  total: number;
  counts: Record<StarCount, number>;
}

/** Položka seznamu adminu; `content` nese jen detail `GET /:id` (celý původní text), seznam nikdy. */
export interface GoogleReviewAdminItem {
  id: string;
  origin: GoogleReviewOrigin;
  authorName: string;
  excerpt: string;
  content?: string;
  reviewDate: string;
  destinationId: string | null;
  destinationName: string | null;
  destinationLocked: boolean;
}

/** Destinace pro přiřazení z textu (F2.7); úroveň se určuje `isCountryLevel` z `routes.ts`. */
export interface DestinationForMatch {
  id: string;
  name: string;
  parentId: string | null;
  showAsCountry: boolean;
  isPublished: boolean;
}

/**
 * Efektivní souhrn Googlu: export má přednost, jinak ruční čísla z Nastavení.
 * `profileUrl` = odkaz na profil (web), `writeReviewUrl` = odkaz pro napsání recenze
 * (dotazník, F2.10). Skládá ho `resolveGoogleSummary`.
 */
export interface EffectiveGoogleSummary {
  source: "export" | "manual";
  score: number | null;
  count: number | null;
  counts: Record<StarCount, number> | null;
  asOf: string | null;
  profileUrl: string | null;
  writeReviewUrl: string | null;
}

/**
 * Normalizace pro párování a otisk autora: NFKD, bez diakritických znamének, malá
 * písmena, všechno mimo písmena a číslice na mezeru, sloučené mezery, `trim`.
 */
export function normalizeForMatch(value: string): string {
  return value
    .normalize("NFKD")
    .replace(/\p{M}+/gu, "")
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, " ")
    .trim();
}

/**
 * Otisk autora odstraněné ruční recenze (F2.3b): SHA-256 hex z `normalizeForMatch(name)`.
 * Jméno samo v DB nezůstane, otisk stačí, aby import tutéž recenzi nevrátil.
 */
export async function authorFingerprint(name: string): Promise<string> {
  const bytes = new TextEncoder().encode(normalizeForMatch(name));
  const digest = await crypto.subtle.digest("SHA-256", bytes);
  return Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, "0")).join("");
}

const STAR_COUNTS: readonly StarCount[] = [5, 4, 3, 2, 1];

/**
 * Známka exportu: průměr hvězd všech recenzí (i bez textu) na jedno desetinné místo,
 * poloviny nahoru; `null` při nula recenzích. Počítá se v celých číslech — `4.85 * 10`
 * v plovoucí čárce není 48,5 a `Math.round` by polovinu zaokrouhlil náhodně.
 */
export function exportScore(counts: Record<StarCount, number>): number | null {
  let total = 0;
  let stars = 0;
  for (const s of STAR_COUNTS) {
    total += counts[s];
    stars += s * counts[s];
  }
  if (total === 0) return null;
  // round_half_up(10 * stars / total) = floor((20 * stars + total) / (2 * total))
  return Math.floor((20 * stars + total) / (2 * total)) / 10;
}
